import { useTranslator } from "@eo-locale/react";

import { Alert, Box, Flex } from "@/storybook-components";
import ListCard from "./list-card";
import { scrollIntoView } from "@/lib";
import type { Projects } from "@/types";
import { setCurrentPage } from "@/redux/features/projects/slice";
import Card from "@/theme/components/card";
import Pagination from "@/theme/components/pagination";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { FINDER_DESKTOP, FINDER_WIDE } from "./breakpoints";

const List = ({ projects }: Projects): JSX.Element => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslator();
  const { queryFilter } = useAppSelector((state) => state.projectFinder);
  const { currentPage } = useAppSelector((state) => state.projects);

  const { enablePagination, locales, perPage } = queryFilter;

  const renderProjects = () => {
    const totalPages = projects.length;

    if (totalPages <= 0) {
      return (
        <Alert.Root status="info" borderRadius="md">
          {locales["projects.noContent"] ||
            translate("projectFinder.noContent")}
        </Alert.Root>
      );
    }

    if (enablePagination) {
      // get index of first & last project and update the projects array
      const indexOfLastProject = currentPage * perPage;
      const indexOfFirstProject = indexOfLastProject - perPage;

      projects = projects.slice(indexOfFirstProject, indexOfLastProject);
    }

    const paginate = (currentPage: number): void => {
      scrollIntoView();
      dispatch(setCurrentPage(currentPage));
    };

    return (
      <>
        <Flex
          wrap="wrap"
          w="full"
          className="ehq-projectsContainer"
          css={{
            flexDirection: "column",
            [FINDER_WIDE]: { flexDirection: "row" }
          }}
        >
          {projects.map((project) => renderProject(project))}
        </Flex>
        {enablePagination ? (
          <Flex
            py="15px"
            w="full"
            wrap="wrap"
            justify="center"
            className="ehq-paginationContainer"
          >
            <Pagination
              paginate={paginate}
              total={totalPages}
              currentPage={currentPage}
              pageSize={perPage}
              key="pagination"
            />
          </Flex>
        ) : null}
      </>
    );
  };

  const renderProject = (project) => {
    return (
      <Box
        mb={[0, 0, 3]}
        minW={0}
        fontSize="sm"
        className="ehq-projectCardContainer"
        css={{
          width: "100%",
          [FINDER_WIDE]: { width: "50%" },
          [FINDER_DESKTOP]: { width: "33.33%" }
        }}
        key={project.id}
      >
        <Card m={2} display="block" height="100%" key={project.id}>
          <ListCard project={project} />
        </Card>
      </Box>
    );
  };
  return renderProjects();
};

export default List;
